import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import './BrandInvitesPanel.css';

/**
 * Admin → Brands: invite a new brand, then review what they submit.
 * Secrets are never shown here, only "saved, ending …1234".
 */

const API = `${API_BASE_URL}/api/brand-onboarding/admin/invites`;

const STATUS_LABEL = {
  open: 'Waiting for them',
  submitted: 'Ready to review',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  rejected: 'Rejected',
};

function fmtDate(v) {
  return v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

async function send(url, method, body) {
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || 'Request failed.');
  return data;
}

function Review({ invite, busy, onAct }) {
  const s = invite.submission || {};
  const [slug, setSlug] = useState(invite.suggestedSlug || '');
  const [note, setNote] = useState('');
  const sec = invite.secrets || {};
  const rows = [
    ['Brand', s.brandName],
    ['Results label', s.resultsLabel || `${s.brandName || ''} Results`],
    ['Website', s.website],
    ['Owner', [s.ownerName, s.ownerEmail && `(${s.ownerEmail})`].filter(Boolean).join(' ')],
    ['Signed in as', invite.claimedBy],
    ['Whop company', s.whopCompanyId],
    ['API key', sec.whopApiKey?.set ? `Saved, ending …${sec.whopApiKey.last4}` : 'Missing'],
    ['Webhook secret', sec.whopWebhookSecret?.set ? `Saved, ending …${sec.whopWebhookSecret.last4}` : 'Missing'],
    ['Checkout', s.checkoutUrl],
    ['Team (get access)', (s.staffEmails || []).join(', ')],
    ['Trade alert emails', (s.tradeWatchSupportEmails || []).join(', ')],
    ['Embed domains', (s.embedDomains || []).join(', ')],
    ['Notes', s.notes],
  ];
  return (
    <div className="bi-review">
      <div className="bi-review-head">
        {s.logoDataUrl ? <img src={s.logoDataUrl} alt="" className="bi-logo" /> : null}
        {s.accentColor ? <span className="bi-swatch" style={{ background: s.accentColor }} title={s.accentColor} /> : null}
      </div>
      <dl className="bi-dl">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt>{k}</dt>
            <dd>{v || '—'}</dd>
          </React.Fragment>
        ))}
      </dl>
      <div className="bi-actions">
        <label className="bi-inline">
          <span>Slug</span>
          <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="gemalgo" />
        </label>
        <button type="button" className="ae-btn ae-btn-primary" disabled={busy || !slug} onClick={() => onAct('approve', { slug })}>
          Approve &amp; create brand
        </button>
      </div>
      <div className="bi-actions">
        <input
          className="bi-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What needs to change? (they'll see this)"
        />
        <button type="button" className="ae-btn" disabled={busy || !note.trim()} onClick={() => onAct('request-changes', { note })}>
          Send back
        </button>
        <button type="button" className="ae-btn ae-btn-ghost" disabled={busy} onClick={() => onAct('reject', { note })}>
          Reject
        </button>
      </div>
    </div>
  );
}

export default function BrandInvitesPanel({ onApproved }) {
  const [invites, setInvites] = useState([]);
  const [secretsConfigured, setSecretsConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newLink, setNewLink] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await send(API, 'GET');
      setInvites(data.invites || []);
      setSecretsConfigured(data.secretsConfigured !== false);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setError('');
    setNotice('');
    setBusyId('new');
    try {
      const data = await send(API, 'POST', { invitedEmail: newEmail.trim(), note: newNote.trim() });
      setNewLink(`${window.location.origin}/brand-onboarding?invite=${data.token}`);
      setNewEmail('');
      setNewNote('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const act = async (invite, action, body) => {
    if (action === 'reject' && !window.confirm('Reject this brand? Their saved Whop keys are deleted.')) return;
    if (action === 'revoke' && !window.confirm('Revoke this link? It stops working immediately.')) return;
    setError('');
    setNotice('');
    setBusyId(invite.id);
    try {
      if (action === 'revoke') {
        await send(`${API}/${invite.id}`, 'DELETE');
        setNotice('Link revoked.');
      } else {
        const data = await send(`${API}/${invite.id}/${action}`, 'POST', body);
        if (action === 'approve') {
          setNotice(`Created brand "${data.entitySlug}". Access granted to ${data.granted.length} people.`);
          if (onApproved) onApproved();
        } else {
          setNotice(action === 'reject' ? 'Rejected.' : 'Sent back with your note.');
        }
      }
      setOpenId(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(newLink);
      setNotice('Link copied.');
    } catch { /* still selectable */ }
  };

  const visible = invites.filter((i) => i.status !== 'rejected' || openId === i.id);

  return (
    <section className="ae-card bi-panel">
      <h2 className="ae-card-title">Brand invites</h2>
      <p className="ae-sub">
        Send a brand owner a link to set up their brand: name, logo, colors and their Whop keys.
        Keys are encrypted the moment they&apos;re entered and never shown again. Nothing goes live until you approve it.
      </p>

      {!secretsConfigured ? (
        <div className="ae-error" role="alert">
          Secret encryption isn&apos;t configured on this server (BRAND_SECRETS_KEY or NT_ENCRYPTION_KEY), so brands can&apos;t save Whop keys yet.
        </div>
      ) : null}
      {error ? <div className="ae-error" role="alert">{error}</div> : null}
      {notice ? <div className="ae-notice" role="status">{notice}</div> : null}

      <div className="bi-create">
        <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Their email (optional, locks the link to it)" />
        <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Note for you, e.g. GemAlgo" />
        <button type="button" className="ae-btn ae-btn-primary" onClick={create} disabled={busyId === 'new'}>
          {busyId === 'new' ? 'Creating…' : 'Invite a brand'}
        </button>
      </div>

      {newLink ? (
        <div className="bi-newlink">
          <strong>Send this link privately. It&apos;s shown only once and expires in 7 days.</strong>
          <div className="bi-newlink-row">
            <code>{newLink}</code>
            <button type="button" className="ae-btn" onClick={copyLink}>Copy</button>
            <button type="button" className="ae-btn" onClick={() => setNewLink('')}>Done</button>
          </div>
        </div>
      ) : null}

      {loading ? <p className="ae-empty">Loading…</p> : null}
      {!loading && !visible.length ? <p className="ae-empty">No invites yet.</p> : null}

      <ul className="bi-list">
        {visible.map((inv) => {
          const s = inv.submission || {};
          const label = inv.expired ? 'Expired' : STATUS_LABEL[inv.status] || inv.status;
          const reviewable = inv.status === 'submitted';
          return (
            <li key={inv.id} className={`bi-item bi-item--${inv.expired ? 'expired' : inv.status}`}>
              <div className="bi-row">
                {s.logoDataUrl ? <img src={s.logoDataUrl} alt="" className="bi-logo bi-logo--small" /> : <span className="bi-logo bi-logo--small bi-logo--empty" />}
                <div className="bi-main">
                  <strong>{s.brandName || inv.note || 'New brand'}</strong>
                  <span className="bi-meta">
                    {inv.claimedBy || inv.invitedEmail || 'Not opened yet'}
                    {' · '}
                    {inv.status === 'approved' ? `Brand "${inv.entitySlug}"` : `Expires ${fmtDate(inv.expiresAt)}`}
                  </span>
                </div>
                <span className={`bi-badge bi-badge--${inv.expired ? 'expired' : inv.status}`}>{label}</span>
                {reviewable ? (
                  <button type="button" className="ae-btn" onClick={() => setOpenId(openId === inv.id ? null : inv.id)}>
                    {openId === inv.id ? 'Close' : 'Review'}
                  </button>
                ) : null}
                {['open', 'changes_requested'].includes(inv.status) ? (
                  <button type="button" className="ae-btn ae-btn-ghost" disabled={busyId === inv.id} onClick={() => act(inv, 'revoke')}>
                    Revoke
                  </button>
                ) : null}
              </div>
              {reviewable && openId === inv.id ? (
                <Review invite={inv} busy={busyId === inv.id} onAct={(action, body) => act(inv, action, body)} />
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
